/**
 * SUPERBA SEARCH — header dropdown
 * ---------------------------------------------------------------------------
 * Searches assets/search-index.json (built by tools/build-index.js), which holds
 * one row per PIECE — colourways already collapsed. So "oli" returns Oli Large
 * Sofa once, not 49 times.
 *
 * Row shape (keys kept short to keep the file small):
 *   r range · g colour-group · t title · h hero handle · p hero price
 *   img hero image · d [w,d,h] cm · n cover count · c [[name, handle, price], …]
 *
 * Swatch images are NOT in the index — they're fetched on demand for whatever is
 * on screen, through the same Storefront query and sessionStorage cache the PDP
 * colour picker already uses (see snippets/colour-picker-cro.liquid).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-superba-search]');
  if (!root || typeof MiniSearch === 'undefined') return;

  var INDEX_URL = root.dataset.indexUrl;
  var SHOP = root.dataset.shopDomain;
  var TOKEN = root.dataset.storefrontToken;
  var SEARCH_URL = root.dataset.searchUrl || '/search';
  /* The live collections shelf. Overridable from the snippet so the page or the
     section id can move without touching this file. */
  var COLLECTIONS_URL = root.dataset.collectionsUrl || '';
  var COLLECTIONS_PAGE = COLLECTIONS_URL.split('?')[0];
  var CONTACT_URL = root.dataset.contactUrl || '/pages/contact-us';
  var TRENDING = (root.dataset.trending || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  var MAX_RESULTS = 24;  // plenty to scroll; 'View all' covers the rest

  var input = document.querySelector('.header-search__input');
  var panel = root.querySelector('.ss__panel');
  if (!input || !panel) return;

  var mini = null;
  var rows = null;
  var loading = null;
  var activeIndex = -1;
  /* What is on screen: idle vs a query, plus any rail filter the shopper clicked */
  var state = { query: '', hits: [], filter: null, idle: true };

  /* ---------------------------------------------------------------- index -- */

  function loadIndex() {
    if (loading) return loading;
    loading = fetch(INDEX_URL)
      .then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      })
      .then(function (data) {
        rows = data.map(function (row, i) {
          return {
            id: i,
            r: row.r || '',
            t: row.t,
            g: row.g,
            h: row.h,
            p: row.p,
            img: row.img,
            d: row.d,
            n: row.n,
            c: row.c,
            /* searchable derivations */
            words: (row.g || '').replace(/-/g, ' '),
            covers: (row.c || [])
              .map(function (c) { return c[0]; })
              .join(' '),
          };
        });

        mini = new MiniSearch({
          fields: ['t', 'r', 'words', 'covers'],
          storeFields: ['r', 't', 'g', 'h', 'p', 'img', 'd', 'n', 'c'],
          searchOptions: {
            prefix: true,
            fuzzy: 0.2,
            combineWith: 'AND',
            boost: { t: 4, r: 3, words: 2, covers: 1 },
          },
        });
        mini.addAll(rows);
        return rows;
      })
      .catch(function (err) {
        console.error('Superba search: could not load index', err);
        loading = null;
        throw err;
      });
    return loading;
  }

  /* ------------------------------------------------------------- matching -- */

  /* Work out which cover the shopper is asking for, if any.

     A query usually names the piece AND the colour together — "oli modular
     marfa pluto". So take out the words the piece's own name already accounts
     for, and match what is left against the cover names. Matching the WHOLE
     query against a cover fails here: "oli" and "modular" appear nowhere in
     "Marfa Pluto", so every card fell back to its hero colourway.

     Two guards, both learned the hard way:

     1. If the piece name accounts for every word, this is not a colour query at
        all — return null. Without it "oli" matches inside "Marfa Olivia" and
        every Oli card swaps to a cover nobody asked for.
     2. Match whole words by prefix, never substrings anywhere in the string. */
  function matchedCover(row, query) {
    var words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return null;

    var piece = (row.r + ' ' + row.t + ' ' + row.words).toLowerCase().split(/\s+/);
    var rest = words.filter(function (w) {
      return !piece.some(function (pw) { return pw.indexOf(w) === 0; });
    });
    if (!rest.length) return null;   /* the query only described the piece */

    for (var i = 0; i < row.c.length; i++) {
      var parts = row.c[i][0].toLowerCase().split(/\s+/);
      var all = rest.every(function (w) {
        return parts.some(function (cw) { return cw.indexOf(w) === 0; });
      });
      if (all) return row.c[i];
    }
    return null;
  }

  /* Default order: biggest piece first.

     Width is the honest proxy for size in furniture — a 305cm corner sofa is
     the flagship of a range, an 84cm footstool is an accessory — so this puts
     what someone is most likely shopping for at the top. Ties go to the dearer
     piece, which is what separates the six 305cm Oli Modular sofas sensibly.

     Every row in the index has a width today; a row without one sorts to the
     end rather than jumping to the front. */
  function bySize(a, b) {
    var aw = (a.d && a.d[0]) || 0;
    var bw = (b.d && b.d[0]) || 0;
    if (bw !== aw) return bw - aw;
    return (b.p || 0) - (a.p || 0);
  }

  /* Relevance leads; size orders within it.

     Sorting purely by size was wrong. "oli" matches 103 pieces once MiniSearch's
     fuzzy matching is counted, and the biggest of those are Jasmine and Goodman
     sofas — the actual Oli range ended up below the fold. So scores are bucketed
     into quarters of the top score: near-equal matches count as equally relevant
     and run biggest first, while a weak fuzzy match stays below a strong one.

     Four buckets, measured, not guessed. At eight the six 305cm Oli Modular
     sofas split across bands and stop reading as one group, which is exactly the
     case this ordering exists to serve. */
  var BANDS = 4;

  function search(query) {
    if (!mini) return { all: [] };
    var hits = mini.search(query);
    if (!hits.length) return { all: [] };
    var top = hits[0].score || 1;
    return {
      /* Pairs rather than tagging the rows: rows[] is indexed by the ids
         MiniSearch returns and is reused by every later search, so it must not
         be mutated or reordered. */
      all: hits
        .map(function (h) {
          return { row: rows[h.id], band: Math.round((h.score / top) * BANDS) };
        })
        .sort(function (a, b) { return (b.band - a.band) || bySize(a.row, b.row); })
        .map(function (x) { return x.row; }),
    };
  }

  /* ----------------------------------------------------------------- rail -- */

  /* Categories are read off the colour-group slug — "oli-modular-left-armchair"
     is an armchair, "flo-coffee-table" a table. Shopify has no category field
     worth using: product_type is "sofa" for all 2,960 upholstery products and
     blank for everything else.

     ORDER MATTERS, first match wins:
       footstool before stool  — or every footstool becomes a bar stool
       chair    before corner  — a corner chair is a chair, not a corner sofa
       u shape  with corner    — the Luxe Open U Shapes are large corner sofas

     Anything unmatched lands in 'Other', which is hidden from the rail. Check
     that bucket after adding new product lines: tools/build-index.js lists
     every colour-group, so a new category is one line here. */
  function category(row) {
    var w = (row.words || '').toLowerCase();
    if (w.indexOf('footstool') > -1) return 'Footstools';
    if (w.indexOf('stool') > -1) return 'Bar stools';
    if (w.indexOf('mirror') > -1) return 'Mirrors';
    if (w.indexOf('table') > -1) return 'Tables';
    if (w.indexOf('pillow') > -1 || w.indexOf('cushion') > -1) return 'Cushions';
    if (w.indexOf('chair') > -1 || w.indexOf('armless') > -1) return 'Armchairs';
    if (w.indexOf('corner') > -1 || w.indexOf('chaise') > -1 ||
        w.indexOf('sectional') > -1 || w.indexOf('u shape') > -1) return 'Corner sofas';
    if (w.indexOf('sofa') > -1) return 'Sofas';
    return 'Other';
  }

  function tally(list, fn) {
    var out = {};
    list.forEach(function (row) {
      var k = fn(row);
      if (k && k !== 'Other') out[k] = (out[k] || 0) + 1;
    });
    return Object.keys(out)
      .sort(function (a, b) { return out[b] - out[a] || a.localeCompare(b); })
      .map(function (k) { return { label: k, count: out[k] }; });
  }

  /* Bold the part of the label the shopper has typed */
  function mark(label, query) {
    var w = query.trim().split(/\s+/)[0];
    if (!w) return esc(label);
    var i = label.toLowerCase().indexOf(w.toLowerCase());
    if (i !== 0) return esc(label);
    return '<b>' + esc(label.slice(0, w.length)) + '</b>' + esc(label.slice(w.length));
  }

  /* Rail rows are buttons: clicking one narrows the results to that range or
     category, clicking it again clears. Counts are always of the FULL match set,
     so they don't shrink to 1 the moment you click something. */
  function railGroup(title, kind, items, query) {
    return '<div class="ss__rail-g"><div class="ss__rail-t">' + title + '</div>' +
      items.map(function (x) {
        var on = state.filter && state.filter.kind === kind && state.filter.value === x.label;
        return '<button type="button" class="ss__rail-i' + (on ? ' is-on' : '') + '"' +
               ' data-kind="' + kind + '" data-value="' + esc(x.label) + '">' +
               '<span>' + (kind === 'range' ? mark(x.label, query) : esc(x.label)) + '</span>' +
               '<span class="ss__rail-n">' + x.count + '</span></button>';
      }).join('') + '</div>';
  }

  function railHTML(all, query) {
    var ranges = tally(all, function (r) { return r.r; }).slice(0, 12);
    var cats = tally(all, category).slice(0, 12);
    var out = '';
    if (ranges.length > 1 || (ranges.length === 1 && ranges[0].count > 1)) {
      out += railGroup('Collections', 'range', ranges, query);
    }
    if (cats.length > 1) out += railGroup('In category', 'cat', cats, query);
    return out;
  }

  /* Mobile has no room for the 214px rail, so the same filters ride as a row of
     chips under the field: swipe sideways for more, tap to narrow, tap the
     active one again to clear. Rendered on every breakpoint and hidden by CSS
     above 749px, so there is one filter state to reason about, not two. */
  function chipsHTML(items) {
    if (!items || !items.length) return '';
    return '<div class="ss__frow">' + items.map(function (x) {
      var on = state.filter && state.filter.kind === x.kind && state.filter.value === x.label;
      return '<button type="button" class="ss__fchip' + (on ? ' is-on' : '') + '"' +
        ' data-kind="' + x.kind + '" data-value="' + esc(x.label) + '">' +
        esc(x.label) + '<span class="ss__fchip-n">' + x.count + '</span></button>';
    }).join('') + '</div>';
  }

  function tagKind(list, kind) {
    return list.map(function (x) { return { label: x.label, count: x.count, kind: kind }; });
  }

  function matchesFilter(row) {
    if (!state.filter) return true;
    return state.filter.kind === 'range'
      ? row.r === state.filter.value
      : category(row) === state.filter.value;
  }

  /* ------------------------------------------------------------ rendering -- */

  function money(pence) {
    return '£' + Number(pence).toLocaleString('en-GB');
  }

  function dims(d) {
    var parts = [];
    if (d[0]) parts.push('W ' + d[0]);
    if (d[1]) parts.push('D ' + d[1]);
    if (d[2]) parts.push('H ' + d[2]);
    return parts.length ? parts.join(' × ') + ' cm' : '';
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* Overlapping circles on the photo — the same control the collection cards
     use (snippets/card-swatches.liquid) and the PDP's "Compare leather types".
     Fewer fit on the narrow row layout, where the thumbnail is ~116px wide. */
  var NARROW = window.matchMedia('(max-width: 600px)');
  /* Four on the row layout: the strip now sits in the text column rather than on
     the thumbnail, so there is room for one more circle and the full
     "+N colours" label beside it. */
  function swatchCount() { return NARROW.matches ? 4 : 6; }

  function cardHTML(result) {
    var row = result.row;
    var cover = result.cover;
    var shown = cover || row.c[0];
    var price = cover ? cover[2] : row.p;
    var meta = '<b>' + esc((cover || row.c[0])[0]) + '</b>';
    var d = dims(row.d);
    var href = '/products/' + encodeURIComponent(shown[1]);

    var ordered = row.c;
    if (cover) {
      ordered = [cover].concat(row.c.filter(function (c) { return c[1] !== cover[1]; }));
    }

    var max = swatchCount();
    var swatches = ordered.slice(0, max).map(function (c) {
      var isMatch = cover && c[1] === cover[1];
      return (
        '<a class="ss__sw' + (isMatch ? ' is-match' : '') + '"' +
        ' href="/products/' + encodeURIComponent(c[1]) + '"' +
        ' data-handle="' + esc(c[1]) + '"' +
        ' data-name="' + esc(c[0]) + '"' +
        ' data-price="' + c[2] + '"' +
        ' title="' + esc(c[0]) + '"><span></span></a>'
      );
    }).join('');

    /* Only label when covers are hidden. Its own class, never .ss__sw — sharing
       that class makes the label inherit the circle's radius, ring and
       background and render as a white blob. */
    var more = row.n - Math.min(max, ordered.length);
    if (more > 0) {
      swatches +=
        '<a class="ss__count" href="' + href + '"' +
        ' title="See all ' + row.n + ' colours">+' + more +
        '<span class="ss__count-w"> colour' + (more === 1 ? '' : 's') + '</span></a>';
    }

    /* The strip overlays the photo, so it needs a positioned wrapper round the
       figure — and it has to stay OUTSIDE the card link, because a swatch is
       itself a link and anchors cannot nest. The figure gets its own anchor so
       clicking the photo still works; it is hidden from assistive tech and out
       of the tab order, since .ss__card already carries the same destination. */
    return (
      '<div class="ss__cell" data-row="' + row.id + '">' +
        '<div class="ss__figwrap">' +
          '<a class="ss__fig" href="' + href + '" tabindex="-1" aria-hidden="true">' +
            (row.img ? '<span class="ss__img" style="background-image:url(' + row.img + ')"></span>' : '') +
          '</a>' +
          '<span class="ss__swatches" data-for="' + row.id + '">' + swatches + '</span>' +
        '</div>' +
        '<a class="ss__card" href="' + href + '" data-row="' + row.id + '">' +
          '<span class="ss__row">' +
            '<span class="ss__name">' + esc(row.t) + '</span>' +
            '<span class="ss__price">' + money(price) + '</span>' +
          '</span>' +
          '<span class="ss__meta">' + meta + (d ? ' <span class="ss__sep">·</span> <span class="ss__dims">' + d + '</span>' : '') + '</span>' +
        '</a>' +
      '</div>'
    );
  }

  /* ---------------------------------------------------------------- help -- */

  /* A dead end is the one moment in search where a person is most likely to give
     up, so it offers a human rather than just apologising. Shared by the
     dropdown and the results page so the wording cannot drift.

     The chat is Shopify Inbox. Its launcher is a button inside
     #shopify-element-wrapper, which since Shopify's 2026 rebuild often sits in a
     shadow root — the same reason the PDP's hide-fix has to pierce shadow DOM to
     find it. If it cannot be found (blocked, still loading, or hidden behind the
     PDP's sticky bar) the contact link beside it still works, which is why both
     are always shown rather than one or the other. */
  /* The launcher, as Shopify Inbox actually renders it today:

       <shopify-chat view="chat">
         #shadow-root (open)
           <div id="shopify-element-wrapper">
             <div class="activator-nudge activator-nudge--left">
               <button part="activator" class="activator" aria-label="Need help?">

     The host element is checked first because that is the real path. The sweep
     over every open shadow root after it is insurance: Shopify rebuilt this
     widget in 2026 and will again, and a dead button here should degrade to the
     contact link rather than to nothing. */
  var CHAT_BUTTON = 'button[part="activator"], button.activator, #shopify-element-wrapper button';

  function findChatActivator() {
    var host = document.querySelector('shopify-chat');
    if (host && host.shadowRoot) {
      var direct = host.shadowRoot.querySelector(CHAT_BUTTON);
      if (direct) return direct;
    }
    var hit = document.querySelector(CHAT_BUTTON);
    if (hit) return hit;
    var all = document.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].shadowRoot) continue;
      hit = all[i].shadowRoot.querySelector(CHAT_BUTTON);
      if (hit) return hit;
    }
    return null;
  }

  function helpHTML() {
    return (
      '<div class="ss__help">' +
        '<p class="ss__help-t">Can\u2019t find what you\u2019re looking for?</p>' +
        '<p class="ss__help-p">Our advisors can talk you through sizes, covers and lead times.</p>' +
        '<span class="ss__help-actions">' +
          '<button type="button" class="ss__help-chat">Chat to an advisor</button>' +
          '<a class="ss__help-alt" href="' + esc(CONTACT_URL) + '">Contact the team</a>' +
        '</span>' +
      '</div>'
    );
  }

  function emptyHTML(heading, hint) {
    return (
      '<div class="ss__empty">' +
        '<p class="ss__empty-h">' + heading + '</p>' +
        (hint ? '<p class="ss__empty-p">' + hint + '</p>' : '') +
        helpHTML() +
      '</div>'
    );
  }

  /* One listener on the document, so it covers the dropdown and the results page
     without either needing to know about the other. */
  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest && ev.target.closest('.ss__help-chat');
    if (!btn) return;
    ev.preventDefault();
    var activator = findChatActivator();
    if (activator) activator.click();
    else window.location.href = CONTACT_URL;   /* no widget: send them somewhere real */
  });

  /* ---------------------------------------------------------------- idle -- */

  /* Shown before anyone types. There is no popularity data in Shopify to sort
     by, so the default is one piece from each of the biggest ranges — a spread
     of the catalogue rather than a guess at what sells. To curate it instead,
     put colour-group slugs in data-trending on the container. */
  function trendingRows() {
    if (TRENDING.length) {
      var picked = TRENDING.map(function (g) {
        return rows.filter(function (r) { return r.g === g; })[0];
      }).filter(Boolean);
      if (picked.length) return picked.slice(0, 6);
    }
    var byRange = {};
    rows.forEach(function (r) {
      if (!r.r) return;
      (byRange[r.r] = byRange[r.r] || []).push(r);
    });
    return Object.keys(byRange)
      .sort(function (a, b) { return byRange[b].length - byRange[a].length; })
      .slice(0, 6)
      .map(function (k) {
        /* Lead each range with a sofa. Sorting on price alone surfaces six
           footstools, which is a poor advert for a sofa company. */
        var list = byRange[k].slice().sort(function (a, b) { return a.p - b.p; });
        var sofa = list.filter(function (r) { return category(r) === 'Sofas'; });
        return (sofa.length ? sofa : list)[0];
      });
  }

  /* Cover families — the first word of every cover name. Real data, and the
     way people actually shop fabric: "have you got it in Marfa?" */
  function coverFamilies() {
    var out = {};
    rows.forEach(function (r) {
      var seen = {};
      (r.c || []).forEach(function (c) {
        var fam = String(c[0]).split(/\s+/)[0];
        if (!fam || seen[fam]) return;
        seen[fam] = 1;
        out[fam] = (out[fam] || 0) + 1;
      });
    });
    return Object.keys(out)
      .sort(function (a, b) { return out[b] - out[a] || a.localeCompare(b); })
      .slice(0, 6);
  }

  /* -------------------------------------------------- collections shelf -- */

  /* The idle state mirrors /pages/collections, read live rather than copied.

     Liquid cannot do this: {% section %} renders a section with its SCHEMA
     DEFAULTS, not the settings saved on that page, so the curated collection
     list would be lost. The Section Rendering API returns the page's real
     section, which we parse for the data and re-render in our own markup — so
     their page-width CSS never lands inside the dropdown, and a rename on their
     side degrades to the product shelf rather than to a blank panel. */
  var collections = null;        /* null = not fetched yet, [] = nothing usable */
  var collectionsPending = null;

  function loadCollections() {
    if (collectionsPending) return collectionsPending;
    if (!COLLECTIONS_URL) return (collectionsPending = Promise.resolve((collections = [])));
    try {
      var raw = sessionStorage.getItem('ss_collections2');
      if (raw) {
        collections = JSON.parse(raw);
        return (collectionsPending = Promise.resolve(collections));
      }
    } catch (e) { /* private mode */ }

    collectionsPending = fetch(COLLECTIONS_URL)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        collections = parseCollections(html);
        try { sessionStorage.setItem('ss_collections2', JSON.stringify(collections)); } catch (e) {}
        return collections;
      })
      .catch(function () { return (collections = []); });
    return collectionsPending;
  }

  function parseCollections(html) {
    if (!html) return [];
    var doc = new DOMParser().parseFromString(html, 'text/html');
    /* If section_id is wrong Shopify returns the whole page rather than an
       error, and the cards are still in there — so this keeps working. */
    /* sections/collection-card-grid.liquid is what /pages/collections actually
       renders. The other two are fallbacks: main-list-collections.liquid, and
       anything else that links to a collection and carries an image — a nav
       link has no <img>, so the menu is not swept up by it. */
    var cards = doc.querySelectorAll('.ccg-card');
    if (!cards.length) cards = doc.querySelectorAll('.collection-card');
    if (!cards.length) cards = doc.querySelectorAll('a[href*="/collections/"]');

    var out = [], seen = {};
    Array.prototype.forEach.call(cards, function (a) {
      var href = a.getAttribute('href');
      /* A card may point at a product instead of a collection (Moroder does).
         Keep it. The mobile menu shelf uses the same rule and the same cache —
         the key is ss_collections2 since this rule changed, so lists saved by
         the old rule (19 tiles) are not reused. Bump it again if this changes. */
      if (!href || !/\/(collections|products)\//.test(href) || seen[href]) return;
      var img = a.querySelector('img');
      if (!img) return;   /* their page skips imageless collections too */
      seen[href] = 1;
      var t = a.querySelector('.ccg-card__name') || a.querySelector('.collection-name');
      var n = a.querySelector('.collection-count');
      out.push({
        href: href,
        title: (t ? t.textContent : img.getAttribute('alt') || '').trim(),
        count: (n ? n.textContent : '').trim(),
        img: thumb(img.getAttribute('src') || ''),
      });
    });
    return out;
  }

  /* Their page asks Shopify for 800px wide; the panel shows them at ~260. */
  function thumb(url) {
    return url.replace(/([?&]width=)\d+/, '$1400');
  }

  function collectionHTML(c) {
    return (
      '<a class="ss__coll" href="' + esc(c.href) + '">' +
        '<span class="ss__coll-t">' + esc(c.title) + '</span>' +
        (c.count ? '<span class="ss__coll-n">' + esc(c.count) + '</span>' : '') +
        '<span class="ss__coll-img">' +
          (c.img ? '<img src="' + esc(c.img) + '" alt="" loading="lazy">' : '') +
        '</span>' +
      '</a>'
    );
  }

  function renderIdle() {
    if (!rows) return;
    state.idle = true;
    state.query = '';

    /* Unfiltered, this is the collections shelf from /pages/collections. Click a
       category in the rail and it becomes a real listing of everything in it —
       products, not collections — so the filtered view still needs picks. */
    var shelf = !state.filter && collections && collections.length;
    var base = state.filter ? rows.filter(matchesFilter) : trendingRows();
    var picks = shelf ? [] : base.slice(0, MAX_RESULTS).map(function (r) { return { row: r, cover: null }; });
    var cats = tally(rows, category).slice(0, 12);
    var rangeCount = tally(rows, function (r) { return r.r; }).length;

    panel.hidden = false;
    panel.innerHTML =
      '<div class="ss__body">' +
        '<aside class="ss__rail">' +
          railGroup('Shop by', 'cat', cats, '') +
          '<div class="ss__rail-g"><div class="ss__rail-t">Covers</div>' +
            '<div class="ss__chips">' +
              coverFamilies().map(function (f) {
                return '<button type="button" class="ss__chip" data-q="' + esc(f) + '">' + esc(f) + '</button>';
              }).join('') +
            '</div>' +
          '</div>' +
        '</aside>' +
        '<div class="ss__results">' +
          chipsHTML(tagKind(cats, 'cat')) +
          headerHTML(state.filter ? state.filter.value : (shelf ? 'Shop by collection' : 'Popular right now')) +
          '<div class="ss__scroll"><div class="ss__grid' + (shelf ? ' ss__grid--coll' : '') + '">' +
            (shelf ? collections.map(collectionHTML).join('') : picks.map(cardHTML).join('')) +
          '</div></div>' +
          '<div class="ss__foot">' +
            (shelf && COLLECTIONS_PAGE
              ? '<a class="ss__all" href="' + esc(COLLECTIONS_PAGE) + '">All collections</a>'
              : '') +
            '<span class="ss__hint">Searching ' + rangeCount + ' collections &middot; ' + rows.length + ' pieces</span>' +
          '</div>' +
        '</div>' +
      '</div>';
    if (picks.length) hydrateSwatches(picks);
  }

  /* Shared results heading, with a way back out of a filter */
  function headerHTML(title) {
    return '<div class="ss__hd">' +
      '<span class="ss__hd-t">' + title +
        (state.filter ? ' <button type="button" class="ss__clear">Clear</button>' : '') +
      '</span>' +
    '</div>';
  }

  function render(_ignored, query, all) {
    activeIndex = -1;
    if (!query) return renderIdle();

    state.idle = false;
    state.query = query;
    state.hits = all;

    if (!all.length) {
      panel.hidden = false;
      panel.innerHTML = emptyHTML(
        'Nothing matches \u201c' + esc(query) + '\u201d',
        'Try a collection like Oli or Lotus, a piece like corner sofa, or a cover like Marfa.');
      return;
    }

    var filtered = all.filter(matchesFilter);
    var results = filtered.slice(0, MAX_RESULTS).map(function (row) {
      return { row: row, cover: matchedCover(row, query) };
    });

    var rail = railHTML(all, query);
    /* Collections first, then categories — the same order and counts the rail
       uses, so the two never disagree. */
    var chips = tagKind(tally(all, function (r) { return r.r; }).slice(0, 12), 'range')
          .concat(tagKind(tally(all, category).slice(0, 12), 'cat'));
    var title = state.filter
      ? esc(state.filter.value)
      : (results[0] && results[0].cover
          ? 'Shown in ' + esc(results[0].cover[0])
          : 'Pieces matching \u201c' + esc(query) + '\u201d');

    panel.hidden = false;
    panel.innerHTML =
      '<div class="ss__body' + (rail ? '' : ' ss__body--norail') + '">' +
        (rail ? '<aside class="ss__rail">' + rail + '</aside>' : '') +
        '<div class="ss__results">' +
          chipsHTML(chips) +
          headerHTML(title) +
          '<div class="ss__scroll"><div class="ss__grid">' +
            results.map(cardHTML).join('') +
          '</div></div>' +
          '<div class="ss__foot">' +
            '<a class="ss__all" href="' + SEARCH_URL + '?q=' + encodeURIComponent(query) + '">' +
              'View all ' + all.length + ' result' + (all.length === 1 ? '' : 's') +
            '</a>' +
            '<span class="ss__keys"><kbd>&uarr;</kbd> <kbd>&darr;</kbd> to move &middot; <kbd>&crarr;</kbd> to open</span>' +
          '</div>' +
        '</div>' +
      '</div>';

    hydrateSwatches(results);
  }

  function redraw() {
    if (state.idle) renderIdle();
    else render(null, state.query, state.hits);
  }

  /* --------------------------------------------------- swatch images (lazy) - */

  var swatchCache = {};

  function cached(handle) {
    if (swatchCache[handle] !== undefined) return swatchCache[handle];
    try {
      var v = sessionStorage.getItem('ss_sw_' + handle);
      if (v) return (swatchCache[handle] = JSON.parse(v));
    } catch (e) { /* private mode */ }
    return undefined;
  }

  function store(handle, value) {
    swatchCache[handle] = value;
    try { sessionStorage.setItem('ss_sw_' + handle, JSON.stringify(value)); } catch (e) { /* full or blocked */ }
  }

  /* One batched Storefront query for every swatch currently on screen. */
  function hydrateSwatches(results, root) {
    var wanted = [];
    results.forEach(function (r) {
      var list = r.row.c.slice(0, swatchCount());
      if (r.cover && list.every(function (c) { return c[1] !== r.cover[1]; })) list.push(r.cover);
      list.forEach(function (c) {
        if (cached(c[1]) === undefined && wanted.indexOf(c[1]) === -1) wanted.push(c[1]);
      });
    });

    paint(results, root);
    if (!wanted.length || !TOKEN || !SHOP) return;

    var parts = wanted.map(function (h, i) {
      return 'p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' +
        ' featuredImage { url(transform: {maxWidth: 600, maxHeight: 600}) }' +
        ' sw: metafield(namespace: "custom", key: "product_details_image") {' +
        '   reference { __typename ... on MediaImage { image { url(transform: {maxWidth: 96, maxHeight: 96, crop: CENTER}) } } ... on GenericFile { url } }' +
        ' } }';
    });

    fetch('https://' + SHOP + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: '{' + parts.join(' ') + '}' }),
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json.data) return;
        wanted.forEach(function (h, i) {
          var p = json.data['p' + i];
          if (!p) return store(h, null);
          var ref = (p.sw && p.sw.reference) || {};
          var swatch =
            ref.__typename === 'MediaImage' ? ref.image.url :
            ref.url ? ref.url : null;
          store(h, { swatch: swatch, photo: p.featuredImage ? p.featuredImage.url : null });
        });
        paint(results, root);
      })
      .catch(function (err) { console.warn('Superba search: swatch fetch failed', err); });
  }

  function paint(results, root) {
    var scope = root || panel;
    results.forEach(function (r) {
      /* When the query matched a cover, show THAT colourway's photo. The index
         only carries the hero image, so this lands once the on-demand fetch
         returns — until then the card shows the hero. */
      if (r.cover) {
        var cd = cached(r.cover[1]);
        if (cd && cd.photo) {
          var cimg = scope.querySelector('.ss__cell[data-row="' + r.row.id + '"] .ss__img');
          if (cimg) cimg.style.backgroundImage = 'url(' + cd.photo + ')';
        }
      }

      var wrap = scope.querySelector('.ss__swatches[data-for="' + r.row.id + '"]');
      if (!wrap) return;
      wrap.querySelectorAll('.ss__sw[data-handle]').forEach(function (el) {
        var data = cached(el.dataset.handle);
        if (!data) return;
        var box = el.firstElementChild;
        if (box && data.swatch) box.style.backgroundImage = 'url(' + data.swatch + ')';
        if (data.photo) el.dataset.photo = data.photo;
      });
    });
  }

  /* ------------------------------------------------- hover colour preview -- */

  /* Desktop only. On touch there is no hover, so a tap just follows the link. */
  panel.addEventListener('mouseover', function (e) {
    var sw = e.target.closest('.ss__sw[data-photo]');
    if (!sw) return;
    var cell = sw.closest('.ss__cell');
    if (!cell) return;
    var img = cell.querySelector('.ss__img');
    var name = cell.querySelector('.ss__meta');
    var price = cell.querySelector('.ss__price');
    if (!name || !price) return;
    if (!cell.dataset.restore) {
      cell.dataset.restore = JSON.stringify({
        img: img ? img.style.backgroundImage : '',
        meta: name.innerHTML,
        price: price.innerHTML,
      });
    }
    if (img) img.style.backgroundImage = 'url(' + sw.dataset.photo + ')';
    var dimsEl = name.querySelector('.ss__dims');
    name.innerHTML = '<b>' + esc(sw.dataset.name) + '</b>' +
      (dimsEl ? ' <span class="ss__sep">\u00b7</span> <span class="ss__dims">' + dimsEl.textContent + '</span>' : '');
    price.innerHTML = money(sw.dataset.price);
  });

  /* Restore on the way out of the whole cell, not off the swatch: the
     "+N colours" label is deliberately not a .ss__sw, so leaving through it
     would otherwise strand the card on a cover nobody is pointing at. */
  panel.addEventListener('mouseout', function (e) {
    var cell = e.target.closest('.ss__cell');
    if (!cell || !cell.dataset.restore) return;
    if (e.relatedTarget && cell.contains(e.relatedTarget)) return;
    var saved = JSON.parse(cell.dataset.restore);
    var img = cell.querySelector('.ss__img');
    if (img) img.style.backgroundImage = saved.img;
    cell.querySelector('.ss__meta').innerHTML = saved.meta;
    cell.querySelector('.ss__price').innerHTML = saved.price;
    delete cell.dataset.restore;
  });

  /* -------------------------------------------------------------- keyboard - */

  function cards() {
    return Array.prototype.slice.call(panel.querySelectorAll('.ss__card, .ss__coll'));
  }

  input.addEventListener('keydown', function (e) {
    var list = cards();
    if (!list.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex += e.key === 'ArrowDown' ? 1 : -1;
      if (activeIndex < 0) activeIndex = list.length - 1;
      if (activeIndex >= list.length) activeIndex = 0;
      list.forEach(function (c, i) { c.classList.toggle('is-active', i === activeIndex); });
      (list[activeIndex].closest('.ss__cell') || list[activeIndex]).scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && activeIndex > -1) {
      e.preventDefault();
      list[activeIndex].click();
    } else if (e.key === 'Escape') {
      panel.hidden = true;
    }
  });

  /* ----------------------------------------------------------------- input - */

  var timer = null;

  function handle() {
    var q = input.value.trim();
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (!q) { state.filter = null; return loadIndex().then(renderIdle).catch(function () {}); }
      loadIndex().then(function () {
        if (q !== state.query) state.filter = null;  /* a new query starts clean */
        render(null, q, search(q).all);
      }).catch(function () {});
    }, 80);
  }

  panel.addEventListener('click', function (e) {
    /* Cover chips run a search rather than navigating — the shopper stays put */
    var chip = e.target.closest('.ss__chip');
    if (chip) {
      e.preventDefault();
      state.filter = null;
      input.value = chip.dataset.q;
      input.focus();
      var res = search(chip.dataset.q);
      return render(null, chip.dataset.q, res.all);
    }

    if (e.target.closest('.ss__clear')) {
      e.preventDefault();
      state.filter = null;
      return redraw();
    }

    var item = e.target.closest('.ss__rail-i, .ss__fchip');
    if (!item) return;
    e.preventDefault();
    var kind = item.dataset.kind, value = item.dataset.value;
    var same = state.filter && state.filter.kind === kind && state.filter.value === value;
    state.filter = same ? null : { kind: kind, value: value };
    redraw();
  });

  /* Rotating a phone crosses the breakpoint, and the strip would otherwise keep
     whichever circle count it was built with — six of them do not fit beside a
     116px thumbnail. */
  function onBreakpoint() { if (!panel.hidden) handle(); }
  if (NARROW.addEventListener) NARROW.addEventListener('change', onBreakpoint);
  else if (NARROW.addListener) NARROW.addListener(onBreakpoint);

  /* Fill the panel. Both fetches at once, so it opens with something real in it
     rather than showing the product shelf and swapping it for collections a
     moment later. After the first open both are cached. */
  function prime() {
    Promise.all([loadIndex(), loadCollections()])
      .then(function () { if (!input.value.trim()) renderIdle(); })
      .catch(function () {});
  }

  input.addEventListener('input', handle);
  input.addEventListener('focus', prime);

  /* Focus is not enough on its own. Tapping the header's search icon opens
     <header-search> without focusing the field on mobile, so the sheet sat empty
     until the shopper tapped into it a second time. Watch the component's own
     open attribute — the same one theme.css styles with .header-search[open] —
     and fill the panel the moment it opens. */
  var component = root.closest('header-search');
  if (component) {
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (component.hasAttribute('open')) prime();
      }).observe(component, { attributes: true, attributeFilter: ['open'] });
    }
    if (component.hasAttribute('open')) prime();
  }

  /* ------------------------------------------------------------- shared -- */

  /* The results page (assets/superba-search-page.js) drives the same engine
     rather than carrying a second copy of the matching, grouping and card
     markup — two copies would drift the first time either is touched. It passes
     its own root element so swatch hydration paints into the page, not in here.

     This is exposed at the end, after the guards above: if the header snippet is
     missing the global never appears, and the page falls back to Shopify's own
     results rather than rendering nothing. */
  window.SuperbaSearch = {
    load: loadIndex,
    rows: function () { return rows; },
    search: search,
    matchedCover: matchedCover,
    category: category,
    tally: tally,
    bySize: bySize,
    cardHTML: cardHTML,
    emptyHTML: emptyHTML,
    /* The idle collections shelf, so /search with no query can show the same
       thing the dropdown does before anyone types. */
    loadCollections: loadCollections,
    collections: function () { return collections; },
    collectionHTML: collectionHTML,
    coverFamilies: coverFamilies,
    hydrate: hydrateSwatches,
    money: money,
    dims: dims,
    esc: esc,
  };

  /* The form submits normally to /search?q=, which the results page takes over. */
})();
